import { useState } from 'react'
import { useTuningStore } from '../store/tuningStore'
import { getNoteNameFromTonic, getMidiNoteInOctave, formatCents, getAbsoluteNoteIndex, calculateTonicOctave, NOTE_NAMES } from '../utils/noteUtils'
import { useAudio } from '../hooks/useAudio'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, X } from 'lucide-react'
import type { ExtraNote } from '../types'

const WHITE_KEY_ABSOLUTE_INDICES = [0, 2, 4, 5, 7, 9, 11] // C, D, E, F, G, A, B

type AddSide = 'left' | 'right' | null

export const Keyboard = () => {
  const {
    selectedNote,
    selectedExtraNoteId,
    activeNotes,
    noteOffsets,
    selectNote,
    tonicNote,
    tonicFrequency,
    referencePitch,
    extraNotes,
    addExtraNote,
    removeExtraNote,
    selectExtraNote,
  } = useTuningStore()
  const { playNoteWithTuning } = useAudio()
  // Use the tonic's octave for calculations
  const tonicOctave = calculateTonicOctave(tonicNote, tonicFrequency, referencePitch)

  // Generate all 12 notes in order starting from tonic (relative index 0-11)
  const allNotesRelative = Array.from({ length: 12 }, (_, i) => i)

  // Determine which notes are white keys (C, D, E, F, G, A, B)
  const isWhiteKey = (relativeIndex: number): boolean => {
    const absoluteIndex = getAbsoluteNoteIndex(relativeIndex, tonicNote)
    return WHITE_KEY_ABSOLUTE_INDICES.includes(absoluteIndex)
  }

  // Check if tonic is a black key
  const tonicIsBlackKey = !isWhiteKey(0)

  // Calculate black key positions relative to white keys
  // Black keys are positioned above the preceding white key
  const blackKeyPositions: { [key: number]: number[] } = {}
  allNotesRelative.forEach((relIdx, idx) => {
    if (isWhiteKey(relIdx) && idx < allNotesRelative.length - 1) {
      const nextRelIdx = allNotesRelative[idx + 1]
      // Check if there's a black key between (gap of 1 semitone means black key follows)
      if (nextRelIdx - relIdx === 1 && !isWhiteKey(nextRelIdx)) {
        if (!blackKeyPositions[relIdx]) {
          blackKeyPositions[relIdx] = []
        }
        blackKeyPositions[relIdx].push(nextRelIdx)
      }
    }
  })

  // Calculate MIDI note from relative index, ensuring correct octave relative to tonic
  const getMidiNoteFromRelative = (relativeIndex: number): number => {
    const absoluteNoteIndex = getAbsoluteNoteIndex(relativeIndex, tonicNote)
    // If the absolute note index is less than tonic, it's in the next octave
    let targetOctave = tonicOctave
    if (absoluteNoteIndex < tonicNote) {
      targetOctave = tonicOctave + 1
    }
    return getMidiNoteInOctave(targetOctave, absoluteNoteIndex)
  }

  const handleKeyClick = (e: React.MouseEvent, relativeIndex: number) => {
    if (e.button === 0) {
      // Left click: select note (convert relative to absolute)
      const absoluteNoteIndex = getAbsoluteNoteIndex(relativeIndex, tonicNote)
      selectNote(absoluteNoteIndex)
    } else if (e.button === 2) {
      // Right click: play note
      e.preventDefault()
      const midiNote = getMidiNoteFromRelative(relativeIndex)
      playNoteWithTuning(midiNote)
    }
  }

  const isNoteActive = (midiNote: number): boolean => {
    return activeNotes.has(midiNote)
  }

  const getAbsoluteNoteFromRelative = (relativeIndex: number): number => {
    return getAbsoluteNoteIndex(relativeIndex, tonicNote)
  }

  const getKeyClasses = (relativeIndex: number, isWhite: boolean): string => {
    const baseClasses = isWhite ? 'piano-key-white' : 'piano-key-black'
    const midiNote = getMidiNoteFromRelative(relativeIndex)
    const isActive = isNoteActive(midiNote)
    const absoluteNoteIndex = getAbsoluteNoteFromRelative(relativeIndex)
    const isSelected = selectedNote === absoluteNoteIndex

    let classes = baseClasses

    if (isActive) {
      classes += ' active'
    }

    if (isSelected) {
      classes += ' selected'
    }

    return classes
  }

  const getOffsetColor = (offset: number): string => {
    if (offset > 0) return 'text-blue-600 font-semibold'
    if (offset < 0) return 'text-rose-600 font-semibold'
    return 'text-muted-foreground'
  }

  // --- Extra notes (added before/after the main octave) ---

  const [addSide, setAddSide] = useState<AddSide>(null)
  const [pickNoteIndex, setPickNoteIndex] = useState(0)
  const [pickOctave, setPickOctave] = useState(tonicOctave - 1)

  const extraMidiNote = (n: ExtraNote) => getMidiNoteInOctave(n.octave, n.noteIndex)
  const baseMidiNotes = allNotesRelative.map(getMidiNoteFromRelative)
  const baseMin = Math.min(...baseMidiNotes)

  // Notes lower than the main octave go on the left, everything else (higher,
  // or overlapping the main range) goes on the right.
  const leftExtraNotes = [...extraNotes]
    .filter((n) => extraMidiNote(n) < baseMin)
    .sort((a, b) => extraMidiNote(a) - extraMidiNote(b))
  const rightExtraNotes = [...extraNotes]
    .filter((n) => extraMidiNote(n) >= baseMin)
    .sort((a, b) => extraMidiNote(a) - extraMidiNote(b))

  const openAddPanel = (side: 'left' | 'right') => {
    setAddSide(side)
    setPickNoteIndex(0)
    setPickOctave(side === 'left' ? tonicOctave - 1 : tonicOctave + 1)
  }

  const confirmAdd = () => {
    addExtraNote(pickNoteIndex, pickOctave)
    setAddSide(null)
  }

  const handleExtraNoteClick = (e: React.MouseEvent, note: ExtraNote) => {
    if (e.button === 0) {
      selectExtraNote(note.id)
    } else if (e.button === 2) {
      e.preventDefault()
      playNoteWithTuning(extraMidiNote(note))
    }
  }

  const getExtraKeyClasses = (note: ExtraNote): string => {
    const isWhite = WHITE_KEY_ABSOLUTE_INDICES.includes(note.noteIndex)
    const baseClasses = isWhite ? 'piano-key-white' : 'piano-key-black bg-gradient-to-b from-gray-900 to-black'
    let classes = `${baseClasses} relative h-full w-9 rounded-t-lg flex-shrink-0`
    if (isNoteActive(extraMidiNote(note))) classes += ' active'
    if (selectedExtraNoteId === note.id) classes += ' selected'
    return classes
  }

  const renderExtraNoteKey = (note: ExtraNote) => (
    <div key={note.id} className="relative h-full">
      <button
        className={getExtraKeyClasses(note)}
        onClick={(e) => handleExtraNoteClick(e, note)}
        onContextMenu={(e) => handleExtraNoteClick(e, note)}
        title="Clic gauche: sélectionner • Clic droit: jouer"
      >
        <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
          <div className={`text-[10px] font-bold mb-0.5 ${WHITE_KEY_ABSOLUTE_INDICES.includes(note.noteIndex) ? '' : 'text-white'}`}>
            {NOTE_NAMES[note.noteIndex]}{note.octave}
          </div>
          <div className={`text-[9px] ${WHITE_KEY_ABSOLUTE_INDICES.includes(note.noteIndex) ? getOffsetColor(note.cents) : 'text-gray-200'}`}>
            {formatCents(note.cents)}¢
          </div>
        </div>
      </button>
      <button
        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-[10px] leading-none flex items-center justify-center z-20"
        title="Supprimer cette note"
        onClick={(e) => {
          e.stopPropagation()
          removeExtraNote(note.id)
        }}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  )

  const renderAddButton = (side: 'left' | 'right') => (
    <button
      className="h-10 w-9 flex-shrink-0 rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:text-primary text-muted-foreground flex items-center justify-center transition-colors self-end"
      title={side === 'left' ? "Ajouter une note avant la gamme" : "Ajouter une note après la gamme"}
      onClick={() => openAddPanel(side)}
    >
      <Plus className="h-4 w-4" />
    </button>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Clavier Microtonal</CardTitle>
        <p className="text-sm text-muted-foreground">Clic gauche: sélectionner • Clic droit: jouer</p>
      </CardHeader>
      <CardContent>
        {addSide && (
          <div className="mb-4 p-3 rounded-lg border bg-muted/50 flex items-end gap-3 flex-wrap animate-fade-in">
            <div className="space-y-1">
              <Label className="text-xs">Note</Label>
              <Select value={String(pickNoteIndex)} onValueChange={(v) => setPickNoteIndex(Number(v))}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_NAMES.map((name, idx) => (
                    <SelectItem key={name} value={String(idx)}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Octave</Label>
              <Input
                type="number"
                value={pickOctave}
                onChange={(e) => setPickOctave(parseInt(e.target.value, 10) || 0)}
                className="w-20"
              />
            </div>
            <Button onClick={confirmAdd} size="sm">
              Ajouter
            </Button>
            <Button onClick={() => setAddSide(null)} variant="ghost" size="sm">
              Annuler
            </Button>
          </div>
        )}

        <div className="bg-muted rounded-lg p-4 border flex justify-center gap-2 overflow-x-auto">
          {/* Extra notes added before the main octave */}
          <div className="flex items-end gap-1 flex-shrink-0" style={{ height: '160px' }}>
            {renderAddButton('left')}
            {leftExtraNotes.map(renderExtraNoteKey)}
          </div>

          <div className="relative flex bg-background rounded-lg flex-shrink-0" style={{ height: '160px', maxWidth: '600px', width: '100%', minWidth: '360px' }}>
            {/* Special case: If tonic is a black key, render it first above a spacer */}
            {tonicIsBlackKey && (
              <div
                key="tonic-black"
                className="relative flex-1 border-r border-border"
                style={{ minWidth: '40px' }}
              >
                {/* Invisible white key spacer for layout */}
                <div className="h-full w-full" style={{ position: 'relative', zIndex: 1 }} />
                {/* Tonic black key positioned at the start (left side of container) */}
                <button
                  className={`${getKeyClasses(0, false)} h-24 w-8 rounded-t-lg`}
                  style={{ left: '10%', top: 0, zIndex: 10, position: 'absolute' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleKeyClick(e, 0)
                  }}
                  onContextMenu={(e) => {
                    e.stopPropagation()
                    handleKeyClick(e, 0)
                  }}
                >
                  <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
                    <div className="text-[10px] font-bold text-white mb-0.5">
                      {getNoteNameFromTonic(0, tonicNote)}
                    </div>
                    <div className={`text-[9px] ${getOffsetColor(noteOffsets[getAbsoluteNoteFromRelative(0)])}`}>
                      {formatCents(noteOffsets[getAbsoluteNoteFromRelative(0)])}¢
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* All notes starting from tonic - white keys as full-width, black keys positioned above */}
            {allNotesRelative.map((relativeIndex) => {
              const absoluteNoteIndex = getAbsoluteNoteFromRelative(relativeIndex)
              const isWhite = isWhiteKey(relativeIndex)

              // Skip tonic if it's a black key (already rendered above)
              if (relativeIndex === 0 && tonicIsBlackKey) {
                return null
              }

              // Only render white keys as full-width keys, black keys are positioned above white keys
              if (!isWhite) {
                return null // Black keys will be rendered above their preceding white keys
              }

              return (
                <div
                  key={`white-${relativeIndex}`}
                  className="relative flex-1 border-r border-border last:border-r-0"
                  style={{ minWidth: '40px' }}
                >
                  <button
                    className={`${getKeyClasses(relativeIndex, true)} h-full w-full`}
                    style={{ position: 'relative', zIndex: 1 }}
                    onClick={(e) => handleKeyClick(e, relativeIndex)}
                    onContextMenu={(e) => handleKeyClick(e, relativeIndex)}
                  >
                    <div className="absolute bottom-0 left-0 right-0 p-2 text-center pointer-events-none">
                      <div className="text-sm font-bold mb-0.5">
                        {getNoteNameFromTonic(relativeIndex, tonicNote)}
                      </div>
                      <div className={`text-[10px] ${getOffsetColor(noteOffsets[absoluteNoteIndex])}`}>
                        {formatCents(noteOffsets[absoluteNoteIndex])}¢
                      </div>
                    </div>
                  </button>

                  {/* Black keys positioned over white keys */}
                  {blackKeyPositions[relativeIndex]?.map((blackRelativeIndex) => {
                    const blackAbsoluteIndex = getAbsoluteNoteFromRelative(blackRelativeIndex)
                    return (
                      <button
                        key={`black-${blackRelativeIndex}`}
                        className={`${getKeyClasses(blackRelativeIndex, false)} h-24 w-8 rounded-t-lg`}
                        style={{ left: '60%', top: 0, zIndex: 10 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleKeyClick(e, blackRelativeIndex)
                        }}
                        onContextMenu={(e) => {
                          e.stopPropagation()
                          handleKeyClick(e, blackRelativeIndex)
                        }}
                      >
                        <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
                          <div className="text-[10px] font-bold text-white mb-0.5">
                            {getNoteNameFromTonic(blackRelativeIndex, tonicNote)}
                          </div>
                          <div className={`text-[9px] ${getOffsetColor(noteOffsets[blackAbsoluteIndex])}`}>
                            {formatCents(noteOffsets[blackAbsoluteIndex])}¢
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )
            })}
          </div>

          {/* Extra notes added after the main octave */}
          <div className="flex items-end gap-1 flex-shrink-0" style={{ height: '160px' }}>
            {rightExtraNotes.map(renderExtraNoteKey)}
            {renderAddButton('right')}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
